import { useMemo, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { TestingHeader } from "@/components/testing/testing-header";
import { buildHistoryColumns } from "@/components/tests/history-columns";
import { HistoryFilters } from "@/components/tests/history-filters";
import { Card, DataTable, EmptyState, LoadError, Pagination, SkeletonTable } from "@/components/ui";
import { useActiveAccountId, useActiveMembership } from "@/hooks/use-customer-context";
import { useCustomerFacilities } from "@/hooks/use-customer-premises";
import { useForeignPropertyReset } from "@/hooks/use-foreign-property-reset";
import { useCustomerSubmissionPage } from "@/hooks/use-customer-tests";
import { submissionLink } from "@/lib/destinations";
import { HISTORY_PAGE_SIZE, historyQueryParams, validateHistorySearch } from "@/lib/history-search";

type CustomerSubmissionListItem = components["schemas"]["CustomerSubmissionListItem"];
type CustomerFacility = components["schemas"]["CustomerFacility"];

export const Route = createFileRoute("/_customer/testing/history/")({
  validateSearch: validateHistorySearch,
  component: HistoryPage,
});

const NO_FACILITIES: readonly CustomerFacility[] = [];

/**
 * History: the active account's tests, sorted, filtered and paged on the
 * server. Every control lives in the URL: the filters, the one-column sort
 * (both halves written on any header click, both absent meaning date, newest
 * first) and the page; a change to the filters or the sort returns to the
 * first page.
 */
function HistoryPage() {
  const { t } = useTranslation("customer");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const accountId = useActiveAccountId();
  const active = useActiveMembership();
  const facilitiesRead = useCustomerFacilities(accountId ? { account_id: accountId } : {});

  // One params object per URL and account, so the read's key and the cache
  // entry it names hold still across renders.
  const params = useMemo(() => historyQueryParams(search, accountId ?? ""), [search, accountId]);
  const read = useCustomerSubmissionPage(params);
  const columns = useMemo(() => buildHistoryColumns(t), [t]);

  const { property, result, from, to, sort, dir, page } = search;
  useForeignPropertyReset(
    property,
    facilitiesRead.isPending || facilitiesRead.isError ? undefined : facilitiesRead.data.items,
  );
  const sorting = useMemo<SortingState>(
    () => [{ id: sort ?? "date", desc: (dir ?? "desc") === "desc" }],
    [sort, dir],
  );

  // Every write passes through the validator, so the URL holds what the page
  // reads back: an empty choice and the first page are the param's absence. A
  // committed day replaces the entry rather than adding one.
  const onSearch = (next: Record<string, unknown>, options?: { replace: boolean }) =>
    void navigate({
      to: "/testing/history",
      search: validateHistorySearch(next),
      ...(options?.replace ? { replace: true } : {}),
    });

  const filters = { property, result, from, to };
  const filtered = Object.values(filters).some((value) => value !== undefined);

  // The table's sort holds exactly one entry (removal and multi-sort are off),
  // so a header click names one column and one direction.
  const onSortingChange = (next: SortingState) => {
    const entry = next[0];
    if (!entry) return;
    onSearch({ ...filters, sort: entry.id, dir: entry.desc ? "desc" : "asc" });
  };

  let body: ReactNode;
  if (read.isError) {
    body = (
      <LoadError
        title={t("history.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  } else if (read.isPending) {
    body = <SkeletonTable columns={columns.length} />;
  } else if (read.data.items.length > 0) {
    const total = read.data.total_count;
    body = (
      <div>
        <DataTable<CustomerSubmissionListItem>
          columns={columns}
          data={read.data.items}
          manualSorting
          enableSortingRemoval={false}
          enableMultiSort={false}
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onRowClick={(row) => void navigate(submissionLink(row.id))}
          rowLink={(row) => submissionLink(row.id)}
        />
        {total > HISTORY_PAGE_SIZE && (
          <Pagination
            currentPage={page ?? 1}
            totalPages={Math.ceil(total / HISTORY_PAGE_SIZE)}
            totalItems={total}
            pageSize={HISTORY_PAGE_SIZE}
            onPageChange={(next) => onSearch({ ...filters, sort, dir, page: next })}
          />
        )}
      </div>
    );
  } else if (filtered || page !== undefined) {
    // A page past the last one reads as no match too, and Reset returns to the first.
    body = (
      <Card>
        <EmptyState
          variant="inline"
          title={t("history.noMatch")}
          actionLabel={t("filters.reset")}
          onAction={() => onSearch({})}
        />
      </Card>
    );
  } else {
    body = (
      <Card>
        <p className="text-sm text-body">{t("history.empty", { org: active?.org_name ?? "" })}</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <TestingHeader active="history" />
      <div className="flex flex-col gap-4">
        <HistoryFilters
          search={search}
          facilities={facilitiesRead.data?.items ?? NO_FACILITIES}
          onSearch={onSearch}
        />
        {body}
      </div>
    </div>
  );
}
